import { useState } from 'react';

interface ScriptShot {
  time: string;
  duration: string;
  scene: string;
  narration: string;
}

const SCRIPT_SHOTS: ScriptShot[] = [
  { time: '00:00-00:30', duration: '30s', scene: '钩子开场 — 三个场景快速切换：会议室讨论区块链、手机滚动新闻、被问PoW/PoS一脸茫然', narration: '"你有没有遇到过这种情况：别人讨论区块链和RWA，你完全插不上嘴……"' },
  { time: '00:30-02:00', duration: '90s', scene: '课程定位 — 三类学员画像卡片飞入 + 三项核心能力清单绿色勾选动画', narration: '"这门课专门为三类人设计：职场人、企业管理者、在校学生……学完你将能做到三件事……"' },
  { time: '02:00-03:00', duration: '60s', scene: '哈希函数动画 — 输入「张三转给李四100元」→ 哈希机器 → 64位输出；改一个字输出完全不同', narration: '"哈希函数是区块链防篡改的核心武器。输入内容哪怕改变一个数字，输出的哈希值就完全不同……"' },
  { time: '03:00-04:30', duration: '90s', scene: 'Merkle树动画 — 从下往上逐步建立：4笔交易→两两哈希→根哈希金色发光；篡改检测红色警报', narration: '"区块链用梅克尔树来组织区块内的所有交易。根哈希就是所有交易的集体指纹……"' },
  { time: '04:30-05:00', duration: '30s', scene: '区块链结构图 — 三个区块链条连接，修改中间区块后续全部变红「链式失效」', narration: '"如果有人想篡改第100个区块，他还要重新计算101、102……在计算上几乎不可能……"' },
  { time: '05:00-06:00', duration: '60s', scene: '课程结构展示 — 四个模块卡片弹出 + 讲师介绍卡片', narration: '"课程分4个模块，共12节课，8课时。模块一打基础，模块二讲共识……"' },
  { time: '06:00-07:00', duration: '60s', scene: 'CTA收尾 — 深紫星空背景，三个信息标签依次点亮：¥299早鸟价 · 12节课永久有效 · 附赠速查卡', narration: '"现在报名，享受早鸟价299元，12节课，8课时，购买后永久有效……"' },
];

const PRODUCTION_SPECS = [
  { icon: 'ri-time-line', label: '总时长', value: '约7分钟' },
  { icon: 'ri-hd-line', label: '分辨率', value: '1920×1080 · 25fps' },
  { icon: 'ri-palette-line', label: '主色调', value: '深紫#1A0050 + 金色#F59E0B' },
  { icon: 'ri-robot-line', label: '数字人', value: '男性·东亚面孔·35-45岁' },
  { icon: 'ri-tools-line', label: '制作工具', value: 'HeyGen / 来画 + After Effects' },
  { icon: 'ri-file-video-line', value: 'MP4 | H.264 | AAC 48kHz', label: '输出格式' },
];

export default function CourseVideoPreview() {
  const [activeShot, setActiveShot] = useState<number | null>(null);
  const [showScript, setShowScript] = useState(false);

  return (
    <div className="bg-gradient-to-br from-[#0A0A1A] to-[#1A0050] rounded-2xl overflow-hidden border border-white/10">
      {/* Video Placeholder with Script Preview */}
      <div className="relative aspect-video bg-[#0A0A1A] flex items-center justify-center overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#7C3AED]/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#F59E0B]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          {/* Grid lines */}
          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(#A78BFA 1px, transparent 1px), linear-gradient(90deg, #A78BFA 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>

        {/* Center content */}
        <div className="relative z-10 text-center px-8">
          <div className="w-20 h-20 rounded-full bg-[#F59E0B]/20 border-2 border-[#F59E0B]/50 flex items-center justify-center mx-auto mb-4 cursor-pointer hover:bg-[#F59E0B]/30 transition-all group">
            <i className="ri-play-fill text-[#F59E0B] text-4xl group-hover:scale-110 transition-transform"></i>
          </div>
          <div className="text-white font-black text-xl mb-2">区块链核心原理精讲</div>
          <div className="text-white/50 text-sm mb-1">RWA技术底座全解析 · 约7分钟</div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-full mt-3">
            <span className="w-2 h-2 bg-[#F59E0B] rounded-full animate-pulse"></span>
            <span className="text-[#F59E0B] text-xs font-semibold">视频制作中 · 脚本已完成</span>
          </div>
        </div>

        {/* Timeline bar at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm px-4 py-2">
          <div className="flex items-center gap-2">
            <span className="text-white/40 text-xs">00:00</span>
            <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full w-0 bg-[#F59E0B] rounded-full" />
            </div>
            <span className="text-white/40 text-xs">07:00</span>
          </div>
        </div>
      </div>

      {/* Script Toggle */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-black text-base flex items-center gap-2">
            <i className="ri-film-line text-[#F59E0B]"></i>
            完整分镜脚本
          </h3>
          <button
            onClick={() => setShowScript(!showScript)}
            className="flex items-center gap-2 px-4 py-2 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-full text-[#F59E0B] text-xs font-bold cursor-pointer hover:bg-[#F59E0B]/25 transition-all whitespace-nowrap"
          >
            <i className={showScript ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
            {showScript ? '收起脚本' : '展开脚本'}
          </button>
        </div>

        {/* Production Specs */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
          {PRODUCTION_SPECS.map((spec) => (
            <div key={spec.label} className="bg-white/5 rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <i className={`${spec.icon} text-[#A78BFA] text-xs`}></i>
                <span className="text-white/40 text-[10px]">{spec.label}</span>
              </div>
              <p className="text-white text-xs font-semibold">{spec.value}</p>
            </div>
          ))}
        </div>

        {/* Script Shots */}
        {showScript && (
          <div className="space-y-2 mt-4">
            <div className="text-white/40 text-xs mb-3 flex items-center gap-2">
              <i className="ri-information-line"></i>
              点击镜头查看详细台词
            </div>
            {SCRIPT_SHOTS.map((shot, idx) => (
              <div key={idx}>
                <button
                  onClick={() => setActiveShot(activeShot === idx ? null : idx)}
                  className="w-full flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 hover:border-[#F59E0B]/30 transition-all cursor-pointer text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center shrink-0">
                    <span className="text-[#F59E0B] text-xs font-black">{idx + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[#F59E0B] text-xs font-bold">{shot.time}</span>
                      <span className="text-white/30 text-[10px]">·</span>
                      <span className="text-white/40 text-[10px]">{shot.duration}</span>
                    </div>
                    <p className="text-white/70 text-xs line-clamp-1">{shot.scene}</p>
                  </div>
                  <i className={`text-white/30 transition-transform ${activeShot === idx ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                </button>
                {activeShot === idx && (
                  <div className="mx-3 p-4 bg-[#1A0050]/50 rounded-b-xl border border-t-0 border-white/10">
                    <div className="mb-3">
                      <div className="text-[#A78BFA] text-[10px] font-bold uppercase tracking-wider mb-1">画面描述</div>
                      <p className="text-white/70 text-xs leading-relaxed">{shot.scene}</p>
                    </div>
                    <div>
                      <div className="text-[#F59E0B] text-[10px] font-bold uppercase tracking-wider mb-1">台词</div>
                      <p className="text-white/80 text-xs leading-relaxed italic">{shot.narration}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Production Guide */}
        <div className="mt-4 p-4 bg-[#7C3AED]/10 border border-[#7C3AED]/20 rounded-xl">
          <div className="flex items-start gap-3">
            <i className="ri-lightbulb-line text-[#A78BFA] text-lg shrink-0"></i>
            <div>
              <div className="text-white font-bold text-xs mb-1">AI视频生成指南</div>
              <p className="text-white/50 text-xs leading-relaxed">
                推荐使用 <strong className="text-[#A78BFA]">HeyGen</strong> 或 <strong className="text-[#A78BFA]">来画</strong> 生成数字人讲师部分；
                信息图动画使用 <strong className="text-[#A78BFA]">After Effects + MotionBro</strong> 或 <strong className="text-[#A78BFA]">Canva Pro</strong>；
                配音增强使用 <strong className="text-[#A78BFA]">Adobe Podcast AI</strong>；
                字幕使用 <strong className="text-[#A78BFA]">Whisper AI</strong> 自动生成SRT后手动校对内嵌。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
